import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { refreshToken } from '../app/modules/auth/core/_requests';
import { type ApiResponse, ApiResponseError } from './core/_models';
import { getProjectEnvVariables } from '../shared/projectEnvVariables';
import { type AuthModel } from '@/app/modules/auth/core/_models';
import { getAuth, removeAuth, setAuth } from '@/app/modules/auth/core/AuthHelpers';
import { jwtDecode } from 'jwt-decode';

const projectEnvVariables = getProjectEnvVariables();

const axiosConfig = {
  baseURL: projectEnvVariables.envVariables.VITE_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
  },
};

const client = axios.create(axiosConfig);

const get = <R = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<R>> => {
  return client.get<ApiResponse<R>>(url, config).then((response) => response.data);
};

const post = <R = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<ApiResponse<R>> => {
  return client.post<ApiResponse<R>>(url, data, config).then((response) => response.data);
};

const put = <R = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<ApiResponse<R>> => {
  return client.put<ApiResponse<R>>(url, data, config).then((response) => response.data);
};

const destroy = <R = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<R>> => {
  return client.delete<ApiResponse<R>>(url, config).then((response) => response.data);
};

const patch = <R = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<ApiResponse<R>> => {
  return client.patch<ApiResponse<R>>(url, data, config).then((response) => response.data);
};

type CustomAxiosRequestConfig = InternalAxiosRequestConfig<any> & { _retry?: boolean };

// Holds the in-flight refresh so concurrent 401s share a single refresh request
// instead of each firing its own (which also avoids invalidating a rotating refresh token).
let refreshPromise: Promise<AuthModel> | null = null;

const getRefreshedAuth = (refreshTokenValue: string): Promise<AuthModel> => {
  if (!refreshPromise) {
    refreshPromise = refreshToken(refreshTokenValue)
      .then((rs) => {
        setAuth(rs.data);
        return rs.data;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// The 10s margin absorbs clock drift (the backend allows none); an unreadable token is refreshed like an expired one.
const isExpiring = (token: string): boolean => {
  try {
    return (jwtDecode(token).exp ?? Infinity) * 1000 < Date.now() + 10_000;
  } catch {
    return true;
  }
};

// A token handed outside axios (the EventSource ?token=) never reaches the 401 refresh below, so refresh it before use.
const getFreshToken = async (): Promise<string | undefined> => {
  const auth = getAuth();
  if (!auth?.refresh_token || !isExpiring(auth.token)) return auth?.token;
  return (await getRefreshedAuth(auth.refresh_token)).token;
};

const readBinaryErrorEnvelope = async (data: unknown, contentType: string): Promise<ApiResponse | undefined> => {
  const isJsonResponse = contentType.includes('application/json');
  if (!isJsonResponse) return;
  const envelope = (await new Response(data as BodyInit).json()) as ApiResponse | null;
  const isErrorEnvelope = envelope?.success === false && typeof envelope.status === 'number';
  if (!isErrorEnvelope) return;
  return envelope;
};

const onResponse = async (response: AxiosResponse<ApiResponse>): Promise<AxiosResponse<any, any>> => {
  // Falhas de download também usam o envelope JSON com HTTP 200.
  const responseType = response.config?.responseType;
  const isBinaryResponse = responseType === 'blob' || responseType === 'arraybuffer' || responseType === 'stream';
  if (isBinaryResponse) {
    const contentType = String(response.headers['content-type'] ?? '');
    const errorEnvelope = await readBinaryErrorEnvelope(response.data, contentType);
    if (!errorEnvelope) return response;
    response.data = errorEnvelope;
  }

  if (!response.data.success) {
    const originalRequest = response.config as CustomAxiosRequestConfig;
    const auth = getAuth();
    const errorResult = new ApiResponseError(response.data);
    if (errorResult.status === 401 && auth?.refresh_token && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAuth = await getRefreshedAuth(auth.refresh_token);
        originalRequest.headers['Authorization'] = 'Bearer ' + newAuth.token;
        return client(originalRequest);
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.data) {
          return Promise.reject(error.response.data);
        }
        removeAuth();
        return Promise.reject(error);
      }
    }
    return Promise.reject(errorResult);
  }
  //returns the response if it is not an error
  return response;
};

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onRequest = (config: CustomAxiosRequestConfig): CustomAxiosRequestConfig => {
  const auth = getAuth();
  if (auth?.token && !config.url?.includes('refreshtoken')) {
    config.headers.Authorization = auth.type + ' ' + auth.token;
  }
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

export function setupAxios() {
  client.interceptors.request.use(onRequest, onRequestError);
  client.interceptors.response.use(onResponse, onResponseError);
}

export { client, destroy, get, getFreshToken, onResponse, patch, post, put };
